import React, { useEffect, useState } from 'react';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';
import { withStyles } from '@material-ui/core/styles';
import { withNetPlaContext } from '../../../NetPlaContext';
import { FormattedMessage } from 'react-intl';
import * as api from '../../../api/general/users';
import Button from '@material-ui/core/Button';
import { MasterCardIcon, VisaCardIcon, DeleteIcon, AmexCardIcon } from '../../../components/Icons';
import PaymentMethodModal from './PaymentMethodModal';
import LoadingView from '../../../components/LoadingView/LoadingView';
import { USER_PROFILE } from '../../../helper/routes';

const styles = (theme) =>
	({
		root:
		{
			display: 'flex',
			flexDirection: 'column',
			alignItems: 'center',
			width: '100%',
			height: '100%',
			overflowY: 'auto'
		},
		paymentMethodsHeader: {
			display: 'flex',
			flexDirection: 'row',
			width: '100%',
			height: '100px',
			padding: '30px 40px'
		},
		addButton: {
			backgroundColor: 'var(--blue_300)',
			boxShadow: 'unset',
			marginLeft: 'auto',
			color: 'white',
			'&:hover': {
				backgroundColor: 'var(--blue_400)'
			}
		},
		paymentMethodsContainer: {
			position: 'relative',
			width: '100%',
			height: 'calc(100% - 130px)',
			padding: '10px 16px',
			display: 'grid',
			gridTemplateColumns: 'repeat(12, 1fr)',
			overflowY: 'hidden',

			[theme.breakpoints.down('md')]:
			{
				gridTemplateColumns: 'unset',
				padding: '20px'
			},

			'&::-webkit-scrollbar-track': {
				backgroundColor: 'white',
				border: 'solid 10px transparent'
			},

			'&::-webkit-scrollbar': {
				width: '10px',
				height: 0,
				backgroundColor: 'var(--grey_200)', // TODO: replace old color
				border: '10px'
			},

			'&::-webkit-scrollbar-thumb': {
				backgroundColor: 'var(--grey_200)',
				borderRadius: '10px'
			}
		},
		paymentMethodItem: {
			display: 'flex',
			flexDirection: 'column',
			margin: '10px 20px',
			width: '380px',
			height: '200px',
			padding: '20px',
			boxShadow: '0 0 10px 10px rgb(0 0 0 / 10%)',
			borderRadius: '20px',
			cursor: 'pointer',
			'&.selected': {
				border: '2px solid green'
			}
		},
		paymentMethodItemTop: {
			display: 'flex',
			flexDirection: 'row',
			position: 'relative'
		},
		cardIcon: {
			width: '120px',
			height: '60px'
		},
		deleteCard: {
			position: 'absolute',
			right: 0,
			color: 'white',
			cursor: 'pointer'
		},
		paymentMethodItemMiddle: {
			display: 'flex',
			flexDirection: 'row',
			padding: '0 28px',
			marginTop: 'auto',
			marginBottom: 'auto'
		},
		holderName: {
			color: 'var(--blue_400)',
			fontSize: '26px',
			fontWeight: 'bold'
		},
		paymentMethodItemBottom: {
			display: 'flex',
			alignItems: 'center',
			justifyContent: 'space-between',
			flexDirection: 'row',
			paddingLeft: 28,
			marginTop: 'auto'
		},
		cardNumber: {
			color: 'var(--grey_200)',
			fontSize: '16px',
			fontWeight: '500'
		},
		checkMark: {
			width: 24,
			height: 24,
			border: '1px solid var(--blue_300)',
			borderRadius: '50%',
			display: 'flex',
			alignItems: 'center',
			justifyContent: 'center',
			color: 'var(--blue_300)',
			marginLeft: 8
		}
	});

const PaymentMethods = (props) =>
{
	const { classes, user } = props;
	const [ openModal, setOpenModal ] = useState(false);
	const [ paymentMethods, setPaymentMethods ] = useState([]);
	const [ loading, setLoading ] = useState(false);

	useEffect(() =>
	{
		if (user?.type === 'super-admin')
		{
			window.location.href = USER_PROFILE;
		}
	}, [ user ]);

	async function getPaymentMethod()
	{
		try
		{
			setLoading(true);
			const response = await api.getPaymentMethod();

			if (response?.success)
			{
				setPaymentMethods(response.data.paymentMethods);
			}

			setLoading(false);
		}
		catch (e)
		{
			setLoading(false);
			console.log('Failed to get payment methods', e);
		}
	}

	useEffect(() =>
	{
		getPaymentMethod();
	}, []);

	const handleAddPaymentMethod = async () =>
	{
		setOpenModal(false);
		await getPaymentMethod();

	};

	const handleDetachCard = async (paymentMethod) =>
	{
		setLoading(true);
		await api.deletePaymentMethod(paymentMethod);
		await getPaymentMethod();
	};

	return (
		<div className={classes.root}>
			{loading && <LoadingView />}
			<PaymentMethodModal open={openModal} handleClose={() => setOpenModal(false)} handleSave={handleAddPaymentMethod} />
			<div className={classes.paymentMethodsHeader}>
				<Button
					aria-label="Add Payment Method"
					variant="contained"
					className={classes.addButton}
					onClick={() => setOpenModal(true)}
				>
				+	<FormattedMessage id="payment.label.add" defaultMessage="Add Payment Method" />
				</Button>
			</div>
			<div className={classes.paymentMethodsContainer}>
				{paymentMethods && paymentMethods.map((pm, key) =>
					(<div className={classes.paymentMethodItem} key={key} >
						<div className={classes.paymentMethodItemTop}>
							{pm.card.brand === 'visa' && <VisaCardIcon className={classes.cardIcon} />}
							{pm.card.brand === 'mastercard' && <MasterCardIcon className={classes.cardIcon} />}
							{pm.card.brand === 'amex' && <AmexCardIcon className={classes.cardIcon} />}
							<span className={classes.deleteCard} onClick={() => handleDetachCard(pm.id)}>
								<DeleteIcon/>
							</span>
						</div>
						<div className={classes.paymentMethodItemMiddle}>
							{pm.billing_details.name &&
								<span className={classes.holderName}>{pm.billing_details.name}</span>
							}
						</div>
						<div className={classes.paymentMethodItemBottom}>
							<span className={classes.cardNumber}>{`**** **** **** ${pm.card.last4}`}</span>
							{pm.default && (
								<span className={classes.checkMark}>✓</span>
							)}
						</div>
					</div>)
				)}
			</div>
		</div>
	);
};

PaymentMethods.propTypes = {
	classes: PropTypes.object.isRequired,
	user: PropTypes.object.isRequired
};

const mapStateToProps = (state) =>
{
	return {
		user: state.auth.user
	};
};

export default withNetPlaContext(connect(
	mapStateToProps,
	null,
	null
)(withStyles(styles)(PaymentMethods)));